// Hub Manager Onboarding — Founder/CEO/Admin create & manage login
// credentials for Main Hub Managers + Micro Hub Leads.
//   • Create with a manual password OR an emailed invite link (hybrid)
//   • Force password change on first login
//   • Activate / deactivate (block login) without deleting
//   • Re-issue credentials (invite or manual)
import { useEffect, useState } from "react";
import api from "../../services/api";
import { toast } from "sonner";
import useAuthStore from "../../store/authStore";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Loader2, Plus, UserPlus, ShieldCheck, ShieldOff, KeyRound, Copy, Mail, Link2,
} from "lucide-react";

const ORANGE = "#FF6B00";
const ROLE_LABEL = { main_hub_manager: "Main Hub Manager", micro_hub_lead: "Micro Hub Lead" };

export default function ManagerOnboarding() {
  const { user } = useAuthStore();
  const allowed = ["founder", "ceo", "admin"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainHubs, setMainHubs] = useState([]);
  const [microHubs, setMicroHubs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // {email, invite_link, channel}
  const [resetFor, setResetFor] = useState(null); // row
  const [resetMode, setResetMode] = useState("invite");
  const [resetPwd, setResetPwd] = useState("");

  const blankForm = {
    name: "", email: "", phone: "", role: "main_hub_manager",
    credential_mode: "invite", password: "", hub_id: "", assign_as_manager: false,
  };
  const [form, setForm] = useState(blankForm);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: mh }, { data: mch }] = await Promise.all([
        api.get("/admin/managers"),
        api.get("/hierarchy/hubs", { params: { hub_type: "main" } }),
        api.get("/hierarchy/hubs", { params: { hub_type: "micro" } }),
      ]);
      setRows(m.managers || []);
      setMainHubs(mh.hubs || []);
      setMicroHubs(mch.hubs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load managers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (allowed) load(); }, [allowed]);

  const hubOptions = form.role === "micro_hub_lead" ? microHubs : mainHubs;

  const submitCreate = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (form.credential_mode === "manual" && (form.password || "").length < 8) {
      toast.error("Manual password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        role: form.role,
        credential_mode: form.credential_mode,
        password: form.credential_mode === "manual" ? form.password : undefined,
        hub_id: form.hub_id || null,
        assign_as_manager: !!form.hub_id && form.assign_as_manager,
      };
      const { data } = await api.post("/admin/managers/create", payload);
      setCreateOpen(false);
      setForm(blankForm);
      if (data.invite_link) {
        setInviteResult({ email: data.email, invite_link: data.invite_link, channel: data.invite_channel });
      } else {
        toast.success(`Account created for ${data.email}. They'll change the password on first login.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/managers/${row.id}/active`, { active: !row.is_active });
      toast.success(`${row.email} ${row.is_active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const submitReset = async () => {
    if (resetMode === "manual" && (resetPwd || "").length < 8) {
      toast.error("Manual password must be at least 8 characters");
      return;
    }
    try {
      const { data } = await api.post(`/admin/managers/${resetFor.id}/reset-credentials`, {
        mode: resetMode,
        password: resetMode === "manual" ? resetPwd : undefined,
      });
      setResetFor(null);
      setResetPwd("");
      setResetMode("invite");
      if (data.invite_link) {
        setInviteResult({ email: data.email, invite_link: data.invite_link, channel: "reset" });
      } else {
        toast.success(`New password set for ${resetFor.email}. They'll change it on next login.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link);
    toast.success("Invite link copied");
  };

  if (!allowed) {
    return <div className="p-8 text-slate-500" data-testid="managers-forbidden">Only Founder, CEO or Admin can manage manager accounts.</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="manager-onboarding-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <UserPlus className="h-7 w-7" style={{ color: ORANGE }} /> Manager Accounts
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Create &amp; control logins for Main Hub Managers and Micro Hub Leads.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-manager-btn"
                style={{ background: ORANGE }} className="text-white">
          <Plus className="h-4 w-4 mr-1" /> Create Manager
        </Button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-3 text-slate-500"><Loader2 className="animate-spin h-5 w-5" /> Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-slate-400 py-16 border border-dashed rounded-2xl" data-testid="managers-empty">
          No manager accounts yet. Click <b>Create Manager</b> to add one.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50/70 text-left text-slate-500">
              <tr>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Email</th>
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Hub</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`manager-row-${r.email}`} className="hover:bg-orange-50/30">
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.name || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.email}</td>
                  <td className="px-3 py-2">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">{ROLE_LABEL[r.role] || r.role}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.hub_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold w-fit ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{r.is_active ? "Active" : "Deactivated"}</span>
                      {r.credential_status === "invited" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold w-fit">Invite pending</span>}
                      {r.must_change_password && r.credential_status !== "invited" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold w-fit">Must change pwd</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => { setResetFor(r); setResetMode("invite"); setResetPwd(""); }}
                              data-testid={`reset-${r.email}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
                        <KeyRound className="h-3.5 w-3.5" /> Reset
                      </button>
                      <button onClick={() => toggleActive(r)}
                              data-testid={`toggle-active-${r.email}`}
                              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${r.is_active ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>
                        {r.is_active ? <><ShieldOff className="h-3.5 w-3.5" /> Deactivate</> : <><ShieldCheck className="h-3.5 w-3.5" /> Activate</>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Manager Account</DialogTitle>
            <DialogDescription>The manager is forced to set a new password on first login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full name</Label>
                <Input data-testid="mgr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rahul Verma" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="mgr-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="9000000000" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="mgr-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="manager@pickngo.com" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <select data-testid="mgr-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, hub_id: "" })}
                        className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm">
                  <option value="main_hub_manager">Main Hub Manager</option>
                  <option value="micro_hub_lead">Micro Hub Lead</option>
                </select>
              </div>
              <div>
                <Label>Assign to hub (optional)</Label>
                <select data-testid="mgr-hub" value={form.hub_id} onChange={(e) => setForm({ ...form, hub_id: e.target.value, assign_as_manager: !!e.target.value })}
                        className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm">
                  <option value="">— None —</option>
                  {hubOptions.map((h) => <option key={h.hub_id} value={h.hub_id}>{h.name || h.hub_id} ({h.hub_id})</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label>Credential method</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" data-testid="mode-invite" onClick={() => setForm({ ...form, credential_mode: "invite" })}
                        className={`flex-1 inline-flex items-center justify-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg border ${form.credential_mode === "invite" ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`}>
                  <Mail className="h-4 w-4" /> Email invite link
                </button>
                <button type="button" data-testid="mode-manual" onClick={() => setForm({ ...form, credential_mode: "manual" })}
                        className={`flex-1 inline-flex items-center justify-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg border ${form.credential_mode === "manual" ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`}>
                  <KeyRound className="h-4 w-4" /> Set password
                </button>
              </div>
            </div>
            {form.credential_mode === "manual" && (
              <div>
                <Label>Temporary password (min 8 chars)</Label>
                <Input data-testid="mgr-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temp@12345" />
                <p className="text-xs text-slate-400 mt-1">Share this securely. They must change it on first login.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={saving} style={{ background: ORANGE }} className="text-white" data-testid="submit-create-manager">
              {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset dialog */}
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset credentials</DialogTitle>
            <DialogDescription>{resetFor?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <button type="button" onClick={() => setResetMode("invite")} className={`flex-1 text-sm font-semibold px-3 py-2 rounded-lg border ${resetMode === "invite" ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`}>Email invite link</button>
              <button type="button" onClick={() => setResetMode("manual")} className={`flex-1 text-sm font-semibold px-3 py-2 rounded-lg border ${resetMode === "manual" ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`}>Set password</button>
            </div>
            {resetMode === "manual" && (
              <div>
                <Label>New temporary password (min 8 chars)</Label>
                <Input data-testid="reset-password" type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="Temp@12345" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)}>Cancel</Button>
            <Button onClick={submitReset} style={{ background: ORANGE }} className="text-white" data-testid="submit-reset">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite-result dialog */}
      <Dialog open={!!inviteResult} onOpenChange={(o) => !o && setInviteResult(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" style={{ color: ORANGE }} /> Invite link ready</DialogTitle>
            <DialogDescription>
              An invite email was {inviteResult?.channel === "none" ? "NOT sent (email unavailable) — share this link manually" : "sent"} to {inviteResult?.email}. You can also copy the link below.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            <code className="text-xs break-all flex-1" data-testid="invite-link-value">{inviteResult?.invite_link}</code>
            <Button size="sm" variant="outline" onClick={() => copyLink(inviteResult.invite_link)} data-testid="copy-invite-link"><Copy className="h-4 w-4" /></Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setInviteResult(null)} style={{ background: ORANGE }} className="text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
